/**
 * Toast notification helpers.
 *
 * `toast.push(msg, { classes: ['success'] })` mirrors the old
 * @zerodevx/svelte-toast API so code copied from other Abstract apps keeps
 * working; new code should call the named helpers instead.
 */

import { toastStore } from './toastStore';

export function toastSuccess(msg: string, detail?: string): void
{
	toastStore.push('success', msg, detail);
}

export function toastError(msg: string, detail?: string): void
{
	toastStore.push('error', msg, detail);
}

export function toastWarning(msg: string, detail?: string): void
{
	toastStore.push('warning', msg, detail);
}

export function toastInfo(msg: string, detail?: string): void
{
	toastStore.push('info', msg, detail);
}

interface ToastPushOptions
{
	classes?: string[];
	duration?: number;
	initial?: number;
	next?: number;
	dismissable?: boolean;
}

/** Compatibility shim for the @zerodevx/svelte-toast call signature. */
export const toast = {
	push(msg: string, opts?: ToastPushOptions): number
	{
		const cls = opts?.classes?.[0] ?? 'info';
		const type = cls === 'success' ? 'success'
			: cls === 'warning' ? 'warning'
			: cls === 'error' ? 'error'
			: 'info';

		const htmlStripped = msg.replace(/<[^>]*>/g, '');
		const progress = opts?.next != null ? opts.next : undefined;
		return toastStore.push(type, htmlStripped, undefined, opts?.duration, progress);
	},

	pushError(msg: string, opts?: ToastPushOptions): number
	{
		return this.push(msg, { ...opts, classes: ['error'] });
	},

	pop(id?: number): void
	{
		if (id != null) toastStore.dismiss(id);
	},

	set(id: number, opts?: { next?: number }): void
	{
		if (opts?.next != null) toastStore.setProgress(id, opts.next);
	}
};
